/**
 * @element qds-popover
 * Anchored, nonmodal top-layer content with controlled or internal state.
 * @attr {boolean} open - Whether content is open.
 * @attr {boolean} controlled - Emit requests without changing open; the application owns state.
 * @attr {string} for - Anchor ID in the same root, or the owner document.
 * @attr {string} label - Accessible name for the content dialog.
 * @attr {'bottom'|'bottom-start'|'bottom-end'|'top'|'top-start'|'top-end'|'left'|'right'} placement - Preferred placement, default bottom-start.
 * @attr {number} offset - Gap from the anchor in pixels, default 8.
 * @attr {boolean} no-outside-dismiss - Keep open on outside pointer interaction.
 * @attr {boolean} no-escape - Keep open on Escape.
 * @attr {boolean} autofocus - Move focus to content when opened.
 * @property {HTMLElement|null} anchor - Arbitrary anchor; takes precedence over for/slot.
 * @property {boolean} open - Reflects open.
 * @slot anchor - Trigger element when no anchor property or for attribute is supplied.
 * @slot - Arbitrary panel content, including nested controls.
 * @fires open-change - Cancelable request; detail {open, reason}. Controlled consumers set open themselves.
 * @fires toggle - Actual visibility changed; detail {open}.
 * @csspart panel - Popover content surface.
 */
import { loadBaseCSS } from '../shared/button-utils.js';
import { AnchoredOverlayController, overlayActiveElement, overlayContains, overlayFocusable } from '../shared/overlay-utils.js';
import type { OverlayPlacement } from '../shared/overlay-utils.js';

let popoverId = 0;
const placements = new Set<OverlayPlacement>(['bottom', 'bottom-start', 'bottom-end', 'top', 'top-start', 'top-end', 'left', 'right']);
export class QdsPopover extends HTMLElement {
  static get observedAttributes(): string[] { return ['open', 'for', 'label', 'placement', 'offset', 'controlled']; }
  private _anchor: HTMLElement | null = null;
  private _boundAnchor: HTMLElement | null = null;
  private _anchorAttributes: Map<string, string | null> | null = null;
  private _controller: AnchoredOverlayController;
  private _panel: HTMLElement;
  private _reflecting = false;
  private _invoker: HTMLElement | null = null;
  constructor() {
    super(); this.attachShadow({ mode: 'open' });
    this.shadowRoot!.innerHTML = `<style>
      :host { display: inline-block; color: var(--theme-text-primary, #333); font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif); }
      .panel { box-sizing: border-box; position: fixed; inset: auto; margin: 0; padding: var(--qds-popover-padding, 1rem); width: max-content; min-width: 8rem; max-width: calc(100vw - 1rem); overflow: auto; overscroll-behavior: contain; border: 1px solid var(--theme-border-color, #e0e0e0); border-radius: 8px; background: var(--theme-background-primary, #fff); color: inherit; box-shadow: var(--shadow-dropdown, 0 4px 12px rgba(0,0,0,.15)); z-index: var(--qds-popover-z, 1000); }
      [hidden] { display: none !important; }
      .panel:focus-visible { outline: 2px solid var(--theme-accent-primary, #4285f4); outline-offset: 2px; }
    </style><slot name="anchor"></slot><div class="panel" part="panel" role="dialog" tabindex="-1" hidden><slot></slot></div>`;
    this._panel = this.shadowRoot!.querySelector('.panel')!;
    this._panel.id = `qds-popover-panel-${++popoverId}`;
    this._controller = new AnchoredOverlayController(this, this._panel, {
      anchor: () => this.anchor, placement: () => this.placement, offset: () => this.offset,
      outside: () => !this.hasAttribute('no-outside-dismiss'), escape: () => !this.hasAttribute('no-escape'),
      onDismiss: reason => {
        if (reason === 'ancestor-closed' || reason === 'anchor-removed') {
          this._reflecting = true; this.open = false; this._reflecting = false; this._syncOpen();
        } else this.requestOpen(false, reason);
      },
    });
    this.shadowRoot!.querySelector('slot[name="anchor"]')!.addEventListener('slotchange', () => this._bindAnchor());
  }
  get open(): boolean { return this.hasAttribute('open'); }
  set open(value: boolean) { this.toggleAttribute('open', Boolean(value)); }
  get anchor(): HTMLElement | null {
    if (this._anchor) return this._anchor;
    const id = this.getAttribute('for');
    if (id) {
      const root = this.getRootNode() as Document | ShadowRoot;
      return (root.getElementById?.(id) || this.ownerDocument.getElementById(id)) as HTMLElement | null;
    }
    return this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="anchor"]')!.assignedElements()[0] as HTMLElement || null;
  }
  set anchor(value: HTMLElement | null) { this._anchor = value; if (this.isConnected) this._bindAnchor(); }
  get placement(): OverlayPlacement {
    const value = this.getAttribute('placement') as OverlayPlacement;
    return placements.has(value) ? value : 'bottom-start';
  }
  get offset(): number { const value = Number(this.getAttribute('offset') ?? 8); return Number.isFinite(value) ? Math.max(0, value) : 8; }
  connectedCallback(): void {
    loadBaseCSS();
    for (const property of ['open', 'anchor']) if (Object.prototype.hasOwnProperty.call(this, property)) {
      const value = (this as any)[property]; delete (this as any)[property]; (this as any)[property] = value;
    }
    this._bindAnchor(); this._syncOpen(); queueMicrotask(() => { if (this.isConnected) this._bindAnchor(); });
  }
  disconnectedCallback(): void { this._controller.destroy(); this._unbindAnchor(); }
  attributeChangedCallback(name: string): void {
    if (!this._panel) return;
    if (name === 'for' && this.isConnected) this._bindAnchor();
    this._panel.setAttribute('aria-label', this.getAttribute('label') || 'Popover');
    if (this.isConnected && !this._reflecting) this._syncOpen();
    this._controller.reposition();
  }
  show(): void { this.requestOpen(true, 'api'); }
  hide(): void { this.requestOpen(false, 'api'); }
  toggle(): void { this.requestOpen(!this.open, 'trigger'); }
  requestOpen(open: boolean, reason = 'api'): boolean {
    if (this.open === open) return true;
    if (!this.dispatchEvent(new CustomEvent('open-change', { detail: { open, reason }, bubbles: true, composed: true, cancelable: true }))) return false;
    if (!this.hasAttribute('controlled')) this.open = open;
    if (!open && reason === 'escape' && !this.open) this.anchor?.focus({ preventScroll: true });
    return true;
  }
  reposition(): void { this._controller.reposition(); }
  private _anchorClick = (): void => { this.toggle(); };
  private _unbindAnchor(): void {
    if (!this._boundAnchor) return;
    this._boundAnchor.removeEventListener('click', this._anchorClick);
    for (const [name, value] of this._anchorAttributes || []) {
      if (value === null) this._boundAnchor.removeAttribute(name); else this._boundAnchor.setAttribute(name, value);
    }
    this._boundAnchor = null; this._anchorAttributes = null;
  }
  private _bindAnchor(): void {
    const anchor = this.anchor;
    if (anchor !== this._boundAnchor) {
      // Reconnect the controller's anchor observers without changing requested state.
      this._controller.hide();
      this._unbindAnchor(); this._boundAnchor = anchor;
      if (anchor) {
        this._anchorAttributes = new Map(['aria-expanded', 'aria-controls', 'aria-haspopup'].map(name => [name, anchor.getAttribute(name)]));
        anchor.addEventListener('click', this._anchorClick);
        anchor.setAttribute('aria-haspopup', 'dialog'); anchor.setAttribute('aria-controls', this._panel.id);
        if ('ariaControlsElements' in anchor) anchor.ariaControlsElements = [this._panel];
      }
    }
    this._syncOpen();
  }
  private _syncOpen(): void {
    this._panel.setAttribute('aria-label', this.getAttribute('label') || 'Popover');
    const wasOpen = this._controller.open;
    if (this.open) {
      if (!wasOpen) {
        const active = overlayActiveElement(this.ownerDocument);
        this._invoker = !active || active === this.ownerDocument.body || active === this.ownerDocument.documentElement ? this.anchor : active;
      }
      const shown = this._controller.show();
      if (shown && !wasOpen && this.hasAttribute('autofocus')) (overlayFocusable(this._panel)[0] || this._panel).focus({ preventScroll: true });
    } else {
      const returnFocus = wasOpen && overlayContains(this._panel, overlayActiveElement(this.ownerDocument));
      this._controller.hide();
      if (returnFocus && this._invoker?.isConnected) this._invoker.focus({ preventScroll: true });
      this._invoker = null;
    }
    this._boundAnchor?.setAttribute('aria-expanded', String(this._controller.open));
    if (wasOpen !== this._controller.open) this.dispatchEvent(new CustomEvent('toggle', { detail: { open: this._controller.open }, bubbles: true, composed: true }));
  }
}
if (!customElements.get('qds-popover')) customElements.define('qds-popover', QdsPopover);
export default QdsPopover;
